import React from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiMusic,FiMic,FiHeadphones}=FiIcons;

function About() {
  const bandMembers=[
    {name: 'Dallas',role: 'Lead Guitar,Vocals',image: 'https://media.publit.io/file/BigCitySupreme/dallas.png',bio: 'The six-string wizard and lead vocalist. Dallas brings the melodic magic,handles all lead guitar solos,and occasionally remembers to tune his guitar.',icon: FiMusic},
    {name: 'James',role: 'Bass,Lead Vocals',image: 'https://media.publit.io/file/BigCitySupreme/james.png',bio: 'The low-end legend who keeps us grounded (literally). James provides the backbone and shares lead vocals that make everyone else sound better.',icon: FiMic},
    {name: 'Denver',role: 'Rhythm Guitar,Backing Vocals',image: 'https://media.publit.io/file/BigCitySupreme/denver.png',bio: 'The silent assassin of riffs. Denver speaks through his rhythm guitar because words are for lesser mortals. His solos have been known to cause spontaneous air guitar.',icon: FiMusic},
    {name: 'Colin',role: 'Drums,Backing Vocals',image: 'https://media.publit.io/file/BigCitySupreme/colin.png',bio: 'The rhythmic mastermind who hits things professionally. Colin keeps everyone in line with his sticks,backing vocals,and questionable jokes during sound check.',icon: FiHeadphones}
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1 className="text-5xl md:text-7xl font-bold bebas-neune gradient-text mb-6"
            initial={{opacity: 0,y: 50}}
            animate={{opacity: 1,y: 0}}
            transition={{duration: 0.8}}
          >
            THE BAND
          </motion.h1>
          <motion.p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto"
            initial={{opacity: 0,y: 30}}
            animate={{opacity: 1,y: 0}}
            transition={{duration: 0.8,delay: 0.2}}
          >
            Four guys from Edmonton who decided the world needed more alternative rock. The world is still deciding if they were right.
          </motion.p>
        </div>
      </section>

      {/* Band Story */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-16"
            initial={{opacity: 0,y: 30}}
            whileInView={{opacity: 1,y: 0}}
            transition={{duration: 0.8}}
            viewport={{once: true}}
          >
            <h2 className="text-4xl md:text-5xl font-bold bebas-neune gradient-text mb-8">
              Our Supreme Story
            </h2>
            <div className="space-y-6 text-lg text-gray-300 leading-relaxed">
              <p>
                Born in the frozen wasteland of Edmonton,Alberta in 2010,Big City Supreme emerged from the primordial ooze of garage jams and questionable life decisions. What started as four friends making noise in a basement has evolved into four friends making <em className="text-red-400"> professional </em> noise on stages.
              </p>
              <p>
                We're not your typical rock band. We're what happens when alternative attitude meets hard rock power,with a healthy dose of modern swagger thrown in for good measure. Think The Raconteurs had a baby with Queens of the Stone Age,and that baby was raised by Royal Blood in a Canadian garage.
              </p>
              <p>
                Our debut album "Super Deluxe" (2013) put us on the map,and we followed it up with "Urban Dreams" in 2018,showcasing our evolution from garage band to... slightly more professional garage band. Both albums are testaments to our ability to take ourselves just seriously enough to make great music,but not so seriously that we forget to have fun.
              </p>
              <p className="text-yellow-400 font-semibold">
                We're loud,we're proud,and we're probably coming to a venue near you. Consider yourself warned.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Band Members */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2 className="text-4xl md:text-5xl font-bold bebas-neune gradient-text text-center mb-16"
            initial={{opacity: 0,y: 30}}
            whileInView={{opacity: 1,y: 0}}
            transition={{duration: 0.8}}
            viewport={{once: true}}
          >
            Meet the Legends
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-12">
            {bandMembers.map((member,index)=> (
              <motion.div key={member.name}
                className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg p-8 border border-red-600/20 hover:border-red-600/40 transition-all duration-300"
                initial={{opacity: 0,y: 50}}
                whileInView={{opacity: 1,y: 0}}
                transition={{duration: 0.8,delay: index * 0.1}}
                viewport={{once: true}}
                whileHover={{scale: 1.02}}
              >
                <div className="flex items-start space-x-6">
                  <div className="relative">
                    <img src={member.image} alt={member.name} className="w-24 h-24 rounded-full object-cover border-2 border-red-500" />
                    <div className="absolute -bottom-2 -right-2 bg-red-600 rounded-full p-2">
                      <SafeIcon icon={member.icon} className="h-4 w-4 text-white" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold bebas-neune text-white mb-1">
                      {member.name}
                    </h3>
                    <p className="text-red-400 font-semibold mb-3">
                      {member.role}
                    </p>
                    <p className="text-gray-300 leading-relaxed">
                      {member.bio}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Fun Facts */}
      <section className="py-20 bg-gradient-to-r from-red-900/20 to-yellow-900/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h2 className="text-4xl md:text-5xl font-bold bebas-neune gradient-text mb-12"
            initial={{opacity: 0,y: 30}}
            whileInView={{opacity: 1,y: 0}}
            transition={{duration: 0.8}}
            viewport={{once: true}}
          >
            Supreme Facts
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              "We've never missed a show due to weather (Edmonton winters build character)",
              "Our matching outfits are 73% of our stage presence (the other 27% is pure talent)",
              "We once played a gig so loud,the neighbors three blocks away started a noise complaint petition",
              "Dallas has broken exactly 47 guitar strings during live performances (but who's counting?)",
              "James's bass lines have been scientifically proven to cause involuntary head bobbing",
              "Denver communicates primarily through rhythm guitar and meaningful glances",
              "Colin's drum kit has its own Instagram account (@supremebeats_official - not really,but it should)",
              "We're probably the most humble rock gods you'll ever meet (this statement is 100% accurate and not at all ironic)"
            ].map((fact,index)=> (
              <motion.div key={index}
                className="bg-black/50 rounded-lg p-6 border border-yellow-500/20"
                initial={{opacity: 0,x: index % 2===0 ? -30 : 30}}
                whileInView={{opacity: 1,x: 0}}
                transition={{duration: 0.6,delay: index * 0.1}}
                viewport={{once: true}}
              >
                <p className="text-gray-300 leading-relaxed">
                  {fact}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default About;